import {
  Material,
  MathUtils,
  PerspectiveCamera,
  Texture,
  WebGLProgramParametersWithUniforms,
  WebGLRenderer,
} from 'three';

// depth change doesn't seem to behave very well, even with zero added value everything changes
export function addDepthOverride(material: Material, depthChange: number) {
  material.onBeforeCompile = (
    shader: WebGLProgramParametersWithUniforms,
    renderer: WebGLRenderer
  ) => {
    const endIndex = shader.fragmentShader.lastIndexOf('}');
    const depthCode = `
      gl_FragDepth += ${depthChange.toFixed(1)};
    `;
    shader.fragmentShader =
      shader.fragmentShader.slice(0, endIndex) +
      depthCode +
      shader.fragmentShader.slice(endIndex);
  };
}


/**
 * Utility function "memo" class for deferred shader functions.
 * These functions can be utilized in a shader to reverse engineer common attributes found in a forward rendering pipeline.
 *
 * @export
 * @abstract 
 * @class DeferredShaderFunction
 * @typedef {DeferredShaderFunction}
 */
export abstract class DeferredShaderFunction {
  
  
    /**
     * Converts raw depth buffer data to a linear float value
     * 
     * Usage example: water depth blur factor
     *
     * @static
     */
    static DepthToLinearDepth = `
			float depthToLinearDepth( float rawDepthSample, vec2 vertexUV, float camNear, float camFar ) {
				float viewZ = perspectiveDepthToViewZ( rawDepthSample, camNear, camFar );
				return viewZToOrthographicDepth( viewZ, cameraNear, cameraFar );
			}
    `

    
    /**
     * Outputs the fragment world position from screen space coordinates, the UV coordinate and camera projection matrices
     * 
     * Usage example: height based fog effect
     *
     * @static
     * @type {string}
     */
    static ReconstructWorldPosition = `
      vec3 reconstructWorldPosition(float rawDepthSample, vec2 vertexUV, mat4 inverseProjectionMatrix, mat4 inverseViewMatrix) {
          // basic [0,1] → [-1,1]
          float ndcDepth = rawDepthSample * 2.0 - 1.0; 
          vec4 clipPosition = vec4(vertexUV * 2.0 - 1.0, ndcDepth, 1.0);
          vec4 viewPosition = inverseProjectionMatrix * clipPosition;
          viewPosition /= viewPosition.w;
          vec4 worldPosition = viewMatrixInverse * viewPosition;
          return worldPosition.xyz;
      }
    `
}